import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const ReputationPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
            Online Reputation Management
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Protect and enhance your business reputation with proactive online reputation management strategies that build trust and credibility.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Reputation Management Benefits
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-star-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Build Trust</h3>
              <p className="text-gray-600">Establish credibility with positive reviews and professional online presence.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Crisis Prevention</h3>
              <p className="text-gray-600">Proactively address negative feedback before it impacts your business.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-trending-up-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Increase Sales</h3>
              <p className="text-gray-600">Positive reputation directly correlates with higher conversion rates and revenue.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Our Reputation Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-indigo-100 to-purple-100 p-6 rounded-xl">
              <i className="ri-search-2-line text-3xl text-indigo-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Reputation Monitoring</h4>
              <p className="text-sm text-gray-600">24/7 monitoring of online mentions and reviews</p>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-6 rounded-xl">
              <i className="ri-star-smile-line text-3xl text-purple-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Review Management</h4>
              <p className="text-sm text-gray-600">Encourage positive reviews and manage negative ones</p>
            </div>
            <div className="bg-gradient-to-br from-pink-100 to-red-100 p-6 rounded-xl">
              <i className="ri-chat-smile-3-line text-3xl text-pink-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Response Strategy</h4>
              <p className="text-sm text-gray-600">Professional responses to all customer feedback</p>
            </div>
            <div className="bg-gradient-to-br from-red-100 to-orange-100 p-6 rounded-xl">
              <i className="ri-article-line text-3xl text-red-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Content Creation</h4>
              <p className="text-sm text-gray-600">Positive content to enhance your online image</p>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-yellow-100 p-6 rounded-xl">
              <i className="ri-alert-line text-3xl text-orange-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Crisis Management</h4>
              <p className="text-sm text-gray-600">Rapid response to reputation threats</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-100 to-green-100 p-6 rounded-xl">
              <i className="ri-bar-chart-grouped-line text-3xl text-yellow-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Reputation Analytics</h4>
              <p className="text-sm text-gray-600">Detailed reports on reputation metrics and trends</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-6 text-gray-800">Connect With Our Reputation Experts</h3>
          <div className="flex justify-center space-x-6">
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-facebook-fill text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-twitter-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-linkedin-fill text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Reputation?</h2>
          <p className="text-xl mb-8">Let's build a positive online presence that drives trust and business growth!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/consultation"
              className="bg-white text-purple-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Book Free Consultation
            </Link>
            <Link 
              to="/quote"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-purple-600 transition-colors"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReputationPage;